import {
  Alert,
  Box,
  Button,
  Container,
  DialogActions,
  DialogContent,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { useState } from 'react'
import { FiUsers } from 'react-icons/fi'
import '../index.css'
import PreviousRoomDrawer from './PreviousRoomDrawer'
import { getTokenData } from '../utils/ProtectedRoute'

const BACKEND_URL = process.env.REACT_APP_SERVER_SOCKET

const StyledDialogPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: 16,
  backdropFilter: 'blur(10px)',
  background: 'rgba(255, 255, 255, 0.85)',
  boxShadow: '0 8px 32px rgba(31, 38, 135, 0.15)',
  border: '1px solid rgba(255, 255, 255, 0.18)',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)',
  },
}))

export const FormTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderWidth: 2,
    },
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
  },
}))

export const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  fontWeight: 600,
  textTransform: 'none',
  padding: theme.spacing(1.2, 2),
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 14px 0 rgba(0,118,255,0.39)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(0,118,255,0.23)',
  },
}))

export default function RoomModal({ setRoomId }) {
  const [mode, setMode] = useState('choose') // 'choose', 'create', 'join', 'created', 'customId'
  const [roomName, setRoomName] = useState('')
  const [roomIdInput, setRoomIdInput] = useState('')
  const [customRoomId, setCustomRoomId] = useState('')
  const [createdRoomId, setCreatedRoomId] = useState('')
  const [error, setError] = useState('')
  const [copySuccess, setCopySuccess] = useState(false)
  const [open, setOpen] = useState(false)
  const { email: userEmail } = getTokenData()

  const handleCreateRoom = async (customId = null) => {
    setError('')
    try {
      const res = await fetch(`${BACKEND_URL}/api/room/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: roomName,
          userEmail,
          ...(customId && { customRoomId: customId })
        }),
      })
      if (!res.ok) throw new Error('Failed to create room')
      const data = await res.json()
      setCreatedRoomId(data.room.room_id)
      setRoomId(data?.room?.room_id)
      setMode('created')
    } catch {
      setError('Could not create room')
    }
  }

  const handleJoinRoom = async (roomIdInput) => {
    setError('')
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/room/validate/${roomIdInput}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userEmail }),
        }
      )
      const data = await res.json()
      if (data.valid) {
        localStorage.setItem('roomId', roomIdInput)
        setRoomId(roomIdInput)
      } else {
        setError('Invalid Room ID')
        setTimeout(() => {
          setError('')
        }, 1500)
      }
    } catch {
      setError('Invalid Room ID')
    }
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(createdRoomId)
    setCopySuccess(true)
    setTimeout(() => setCopySuccess(false), 1500)
  }

  const handleEnterRoom = () => {
    localStorage.setItem('roomId', createdRoomId)
    setRoomId(createdRoomId)
  }

  return (
    <Box className='login-container' sx={{ minWidth: 'md' }}>
      <Container
        sx={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <StyledDialogPaper sx={{ width: '100%', maxWidth: 400 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <FiUsers size={36} color='#1890ff' />
            <Typography
              variant='h5'
              fontWeight={800}
              mt={1}
              sx={{
                background: 'linear-gradient(90deg, #1890ff, #36cfc9)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {mode === 'choose' && 'Join or Create a Room'}
              {mode === 'create' && 'Create a Room'}
              {mode === 'join' && 'Join a Room'}
              {mode === 'created' && 'Room Created'}
              {mode === 'customId' && 'Custom Room ID'}
            </Typography>
          </Box>

          <DialogContent sx={{ p: 0 }}>
            {mode === 'choose' && (
              <Stack spacing={2} mt={1}>
                <StyledButton
                  variant='contained'
                  onClick={() => setMode('create')}
                >
                  Create Room
                </StyledButton>
                <StyledButton
                  variant='outlined'
                  onClick={() => setMode('customId')}
                >
                  Create Room with Custom ID
                </StyledButton>
                <StyledButton variant='outlined' onClick={() => setOpen(true)}>
                  Join Room
                </StyledButton>
                {open && (
                  <PreviousRoomDrawer
                    open={open}
                    setOpen={setOpen}
                    userEmail={userEmail}
                    handleJoinRoom={handleJoinRoom}
                    error={error}
                  />
                )}
              </Stack>
            )}

            {mode === 'create' && (
              <Stack spacing={2} mt={1}>
                <FormTextField
                  label='Room Name (optional)'
                  fullWidth
                  value={roomName}
                  onChange={(e) => setRoomName(e.target.value)}
                />
                {error && <Alert severity='error'>{error}</Alert>}
              </Stack>
            )}

            {mode === 'customId' && (
              <Stack spacing={2} mt={1}>
                <FormTextField
                  label='Room Name (optional)'
                  fullWidth
                  value={roomName}
                  onChange={(e) => setRoomName(e.target.value)}
                />
                <FormTextField
                  label='Custom Room ID'
                  fullWidth
                  value={customRoomId}
                  onChange={(e) => setCustomRoomId(e.target.value)}
                />
                {error && <Alert severity='error'>{error}</Alert>}
              </Stack>
            )}

            {mode === 'join' && (
              <Stack spacing={2} mt={1}>
                <FormTextField
                  label='Room ID'
                  fullWidth
                  value={roomIdInput}
                  onChange={(e) => setRoomIdInput(e.target.value)}
                />
                {error && <Alert severity='error'>{error}</Alert>}
              </Stack>
            )}

            {mode === 'created' && (
              <Stack spacing={2} mt={1}>
                <Box>
                  <Typography variant='body1'>
                    <b>Room ID:</b>
                  </Typography>
                  <Typography
                    variant='subtitle1'
                    sx={{ wordBreak: 'break-word' }}
                  >
                    {createdRoomId}
                  </Typography>
                </Box>
                <StyledButton variant='outlined' onClick={handleCopy}>
                  {copySuccess ? 'Copied!' : 'Copy Room ID'}
                </StyledButton>
              </Stack>
            )}
          </DialogContent>

          <DialogActions sx={{ justifyContent: 'space-between', mt: 3, p: 0 }}>
            {mode !== 'choose' && (
              <Button
                onClick={() => {
                  setMode('choose')
                  setError('')
                  setRoomIdInput('')
                  setCustomRoomId('')
                }}
              >
                Back
              </Button>
            )}
            {mode === 'create' && (
              <StyledButton variant='contained' onClick={() => handleCreateRoom()}>
                Create
              </StyledButton>
            )}
            {mode === 'customId' && (
              <StyledButton
                variant='contained'
                onClick={() => handleCreateRoom(customRoomId)}
              >
                Create with Custom ID
              </StyledButton>
            )}
            {mode === 'join' && (
              <StyledButton
                variant='contained'
                onClick={() => handleJoinRoom(roomIdInput)}
              >
                Join
              </StyledButton>
            )}
            {mode === 'created' && (
              <StyledButton variant='contained' onClick={handleEnterRoom}>
                Enter Room
              </StyledButton>
            )}
          </DialogActions>
        </StyledDialogPaper>
      </Container>
    </Box>
  )
}
